import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  Button,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Grid,
  Tooltip,
  useTheme,
  useMediaQuery,
  alpha,
  Avatar,
  Card,
  CardContent,
  Stack,
  Collapse,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  FilterList as FilterIcon,
  Business as BusinessIcon,
  LocationOn as LocationOnIcon,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import { Fornecedor, FornecedorFormData } from '../types/fornecedor';
import { FornecedorService } from '../services/fornecedorService';
import FornecedorForm from '../components/forms/FornecedorForm';
import { formatarCPFCNPJ, formatarPhone } from '../utils/formatToView';

interface KpiCardProps {
  titulo: string;
  valor: string | number;
  subtitulo?: string;
  icone: React.ReactNode;
  cor: string;
  bgCor: string;
}

const KpiCard: React.FC<KpiCardProps> = ({ titulo, valor, subtitulo, icone, cor, bgCor }) => (
  <Card elevation={0} sx={{ height: '100%', borderRadius: 2, border: (t) => `1px solid ${alpha(t.palette.divider, 0.8)}` }}>
    <CardContent sx={{ p: 1.25, '&:last-child': { pb: 1.25 } }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" gap={1}>
        <Box minWidth={0}>
          <Typography variant="caption" color="text.secondary" display="block" lineHeight={1.2}>{titulo}</Typography>
          <Typography variant="h6" fontWeight={800} color={cor} lineHeight={1.2}>{valor}</Typography>
          {subtitulo && <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>{subtitulo}</Typography>}
        </Box>
        <Avatar sx={{ bgcolor: bgCor, width: 34, height: 34, '& .MuiSvgIcon-root': { fontSize: 18 } }}>{icone}</Avatar>
      </Box>
    </CardContent>
  </Card>
);

const FornecedoresPage: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [fornecedores, setFornecedores] = useState<Fornecedor[]>([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalFornecedores, setTotalFornecedores] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [cidadeFilter, setCidadeFilter] = useState<string>('');
  const [estadoFilter, setEstadoFilter] = useState<string>('');
  const [vendedorFilter, setVendedorFilter] = useState<string>('');
  const [showFilters, setShowFilters] = useState(false);
  const [openForm, setOpenForm] = useState(false);
  const [editingFornecedor, setEditingFornecedor] = useState<Fornecedor | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [fornecedorToDelete, setFornecedorToDelete] = useState<Fornecedor | null>(null);

  // Buscar fornecedores
  const fetchFornecedores = async () => {
    try {
      setLoading(true);
      const response = await FornecedorService.buscarFornecedoresComFiltros({
        page: page + 1,
        limit: rowsPerPage,
        search: searchTerm,
        cidade: cidadeFilter,
        estado: estadoFilter,
        vendedor: vendedorFilter,
      });

      // Validar se a resposta tem a estrutura esperada
      if (response && response.data && response.pagination) {
        setFornecedores(response.data);
        setTotalFornecedores(response.pagination.total);
      } else {
        console.error('Estrutura de resposta inválida:', response);
        setFornecedores([]);
        setTotalFornecedores(0);
        toast.error('Erro: Resposta da API em formato inválido');
      }
    } catch (error) {
      console.error('❌ Erro ao buscar fornecedores:', error);
      setFornecedores([]);
      setTotalFornecedores(0);
      toast.error('Erro ao carregar fornecedores');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchFornecedores();
  }, [page, rowsPerPage, searchTerm, cidadeFilter, estadoFilter, vendedorFilter]);

  // Handlers
  const handlePageChange = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleRowsPerPageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleSearch = () => {
    setPage(0);
    fetchFornecedores();
  };

  const handleClearFilters = () => {
    setSearchTerm('');
    setCidadeFilter('');
    setEstadoFilter('');
    setVendedorFilter('');
    setPage(0);
  };

  const handleOpenForm = (fornecedor?: Fornecedor) => {
    setEditingFornecedor(fornecedor || null);
    setOpenForm(true);
  };

  const handleCloseForm = () => {
    setOpenForm(false);
    setEditingFornecedor(null);
  };

  const handleSubmitForm = async (data: FornecedorFormData) => {
    try {
      if (editingFornecedor) {
        await FornecedorService.atualizarFornecedor(editingFornecedor.id, data);
        toast.success('Fornecedor atualizado com sucesso!');
      } else {
        await FornecedorService.criarFornecedor(data);
        toast.success('Fornecedor criado com sucesso!');
      }
      handleCloseForm();
      fetchFornecedores();
    } catch (error) {
      console.error('Erro ao salvar fornecedor:', error);
      toast.error('Erro ao salvar fornecedor');
      throw error;
    }
  };

  const handleDeleteClick = (fornecedor: Fornecedor) => {
    setFornecedorToDelete(fornecedor);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!fornecedorToDelete) return;

    try {
      await FornecedorService.excluirFornecedor(fornecedorToDelete.id);
      toast.success('Fornecedor deletado com sucesso!');
      setDeleteDialogOpen(false);
      setFornecedorToDelete(null);
      fetchFornecedores();
    } catch (error) {
      console.error('Erro ao deletar fornecedor:', error);
      toast.error('Erro ao deletar fornecedor');
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setFornecedorToDelete(null);
  };

  return (
    <Box sx={{ p: { xs: 1, md: 1.5 }, mx: 'auto' }}>
      <Paper
        elevation={0}
        sx={{
          mb: 1.5,
          p: { xs: 1.25, md: 1.5 },
          borderRadius: 2,
          background: theme.palette.mode === 'dark'
            ? `linear-gradient(135deg, ${alpha(theme.palette.info.dark, 0.35)} 0%, ${alpha(theme.palette.primary.main, 0.25)} 50%, ${alpha('#1a1a2e', 0.9)} 100%)`
            : `linear-gradient(135deg, ${alpha('#e3f2fd', 0.95)} 0%, ${alpha(theme.palette.primary.light, 0.15)} 45%, ${alpha(theme.palette.primary.main, 0.08)} 100%)`,
          border: `1px solid ${alpha(theme.palette.primary.main, 0.12)}`,
          overflow: 'hidden',
          position: 'relative',
        }}
      >
        <Box sx={{ position: 'absolute', top: -20, right: -10, opacity: 0.08, transform: 'rotate(15deg)' }}>
          <BusinessIcon sx={{ fontSize: 120, color: theme.palette.primary.main }} />
        </Box>
        <Stack direction={{ xs: 'column', sm: 'row' }} alignItems={{ xs: 'flex-start', sm: 'center' }} justifyContent="space-between" spacing={1.25}>
          <Stack direction="row" alignItems="center" spacing={1.25}>
            <Avatar
              sx={{
                width: 40,
                height: 40,
                background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.35)}`,
              }}
            >
              <BusinessIcon sx={{ fontSize: 22 }} />
            </Avatar>
            <Box flex={1} minWidth={0}>
              <Typography variant="h6" fontWeight={800} lineHeight={1.2}>Gerenciamento de Fornecedores</Typography>
              <Typography variant="caption" color="text.secondary">Gerencie os fornecedores do sistema</Typography>
            </Box>
          </Stack>
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => handleOpenForm()} fullWidth={isMobile} size="small" sx={{ minWidth: { md: 140 } }}>
            Novo Fornecedor
          </Button>
        </Stack>
      </Paper>

      {!loading && totalFornecedores > 0 && (
        <Grid container spacing={1} mb={1.5}>
          <Grid item xs={6} md={4}>
            <KpiCard titulo="Total de Fornecedores" valor={totalFornecedores} subtitulo="encontrados na busca" icone={<BusinessIcon sx={{ color: theme.palette.primary.main }} />} cor={theme.palette.primary.main} bgCor={alpha(theme.palette.primary.main, 0.12)} />
          </Grid>
          <Grid item xs={6} md={4}>
            <KpiCard titulo="Página Atual" valor={fornecedores.length} subtitulo={`página ${page + 1}`} icone={<SearchIcon sx={{ color: theme.palette.info.main }} />} cor={theme.palette.info.main} bgCor={alpha(theme.palette.info.main, 0.12)} />
          </Grid>
          <Grid item xs={6} md={4}>
            <KpiCard titulo="Por Página" valor={rowsPerPage} icone={<LocationOnIcon sx={{ color: theme.palette.success.main }} />} cor={theme.palette.success.main} bgCor={alpha(theme.palette.success.main, 0.12)} />
          </Grid>
        </Grid>
      )}

      <Paper elevation={0} sx={{ p: { xs: 1.25, md: 1.5 }, mb: 1.5, borderRadius: 2, border: `1px solid ${theme.palette.divider}`, bgcolor: alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.04 : 0.02) }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: showFilters ? 1 : 0 }}>
          <FilterIcon color="primary" sx={{ fontSize: 18, mr: 0.75 }} />
          <Typography variant="body2" fontWeight={700}>Filtros</Typography>
          <Button size="small" onClick={() => setShowFilters(!showFilters)} sx={{ ml: 'auto', textTransform: 'none' }}>
            {showFilters ? 'Ocultar' : 'Mostrar'} Filtros
          </Button>
        </Box>

        <Collapse in={showFilters}>
          <Grid container spacing={1}>
            <Grid item xs={12} md={3}>
              <TextField fullWidth size="small" label="Buscar" value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} placeholder="Nome, CNPJ, email..." onKeyPress={(e) => e.key === 'Enter' && handleSearch()} />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField fullWidth size="small" label="Cidade" value={cidadeFilter} onChange={(e) => setCidadeFilter(e.target.value)} placeholder="Filtrar por cidade" />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField fullWidth size="small" label="Estado" value={estadoFilter} onChange={(e) => setEstadoFilter(e.target.value)} placeholder="Filtrar por estado" />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField fullWidth size="small" label="Vendedor" value={vendedorFilter} onChange={(e) => setVendedorFilter(e.target.value)} placeholder="Filtrar por vendedor" />
            </Grid>
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button variant="contained" size="small" onClick={handleSearch} startIcon={<SearchIcon />}>Buscar</Button>
                <Button variant="outlined" size="small" onClick={handleClearFilters}>Limpar Filtros</Button>
              </Box>
            </Grid>
          </Grid>
        </Collapse>
      </Paper>

      <Paper elevation={0} sx={{ borderRadius: 2, overflow: 'hidden', border: `1px solid ${theme.palette.divider}` }}>
        <Box sx={{ px: 1.5, py: 0.75, display: 'flex', alignItems: 'center', justifyContent: 'space-between', bgcolor: alpha(theme.palette.primary.main, 0.04), borderBottom: `1px solid ${theme.palette.divider}` }}>
          <Typography variant="body2" fontWeight={700}>Listagem de Fornecedores</Typography>
          {!loading && (
            <Chip size="small" label={`${totalFornecedores} fornecedor${totalFornecedores !== 1 ? 'es' : ''}`} color="primary" variant="outlined" sx={{ height: 22, fontSize: '0.7rem' }} />
          )}
        </Box>
        <TableContainer sx={{ maxHeight: 520 }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow sx={{
                bgcolor: alpha(theme.palette.primary.main, 0.06),
                '& .MuiTableCell-head': {
                  fontWeight: 700,
                  fontSize: '0.68rem',
                  textTransform: 'uppercase',
                  letterSpacing: 0.5,
                  color: 'text.secondary',
                  py: 0.75,
                  px: 1,
                  borderBottom: `2px solid ${alpha(theme.palette.primary.main, 0.15)}`,
                },
              }}>
                <TableCell>Nome</TableCell>
                <TableCell>CNPJ</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Telefone</TableCell>
                <TableCell>Vendedor</TableCell>
                <TableCell>Cidade/Estado</TableCell>
                <TableCell>Ações</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 5 }}>
                    <CircularProgress size={32} />
                    <Typography variant="caption" color="text.secondary" display="block" mt={1}>Carregando fornecedores...</Typography>
                  </TableCell>
                </TableRow>
              ) : (Array.isArray(fornecedores) && fornecedores.length === 0) ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                    <BusinessIcon sx={{ fontSize: 40, color: alpha(theme.palette.text.secondary, 0.25), mb: 1 }} />
                    <Typography variant="subtitle2" fontWeight={600}>Nenhum fornecedor encontrado</Typography>
                  </TableCell>
                </TableRow>
              ) : (
                (Array.isArray(fornecedores) ? fornecedores : []).map((fornecedor) => (
                  <TableRow key={fornecedor.id} hover sx={{ '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.03) } }}>
                    <TableCell sx={{ py: 0.5, fontSize: '0.78rem' }}>{fornecedor.nome}</TableCell>
                    <TableCell sx={{ py: 0.5, fontSize: '0.78rem' }}>{formatarCPFCNPJ(fornecedor.cnpj || '')}</TableCell>
                    <TableCell sx={{ py: 0.5, fontSize: '0.78rem' }}>{fornecedor.email || '-'}</TableCell>
                    <TableCell sx={{ py: 0.5, fontSize: '0.78rem' }}>{formatarPhone(fornecedor.telefone) || '-'}</TableCell>
                    <TableCell sx={{ py: 0.5, fontSize: '0.78rem' }}>{fornecedor.vendedor || '-'}</TableCell>
                    <TableCell sx={{ py: 0.5, fontSize: '0.78rem' }}>
                      {fornecedor.cidade}, {fornecedor.estado}
                    </TableCell>
                    <TableCell align="center" sx={{ py: 0.5, fontSize: '0.78rem' }}>
                      <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                        <Tooltip title="Editar">
                          <IconButton
                            size="small"
                            onClick={() => handleOpenForm(fornecedor)}
                            color="primary"
                          >
                            <EditIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Deletar">
                          <IconButton
                            size="small"
                            onClick={() => handleDeleteClick(fornecedor)}
                            color="error"
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {totalFornecedores > 0 && (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              py: 1.5,
              px: 2,
              bgcolor: alpha(theme.palette.primary.main, 0.04),
              borderTop: `2px solid ${alpha(theme.palette.primary.main, 0.2)}`,
            }}
          >
            <TablePagination
              rowsPerPageOptions={[5, 10, 25, 50]}
              component="div"
              count={totalFornecedores}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handlePageChange}
              onRowsPerPageChange={handleRowsPerPageChange}
              labelRowsPerPage="Linhas por página:"
              labelDisplayedRows={({ from, to, count }) =>
                `${from}-${to} de ${count !== -1 ? count : `mais de ${to}`}`
              }
              sx={{
                '.MuiTablePagination-toolbar': {
                  minHeight: 44,
                  flexWrap: 'wrap',
                  justifyContent: 'center',
                  gap: 2,
                  '& .MuiTablePagination-spacer': { display: 'none' },
                },
                '.MuiTablePagination-selectLabel': { m: 0, fontSize: '0.875rem' },
                '.MuiTablePagination-select': { py: 0.5, pr: 2.5, fontSize: '0.875rem' },
                '.MuiTablePagination-displayedRows': { m: 0, fontSize: '0.875rem' },
                '.MuiTablePagination-actions': { ml: 0.5 },
              }}
            />
          </Box>
        )}
      </Paper>

      {/* Formulário */}
      <FornecedorForm
        open={openForm}
        onClose={handleCloseForm}
        fornecedor={editingFornecedor}
        onSubmit={handleSubmitForm}
      />

      {/* Dialog de confirmação de exclusão */}
      <Dialog open={deleteDialogOpen} onClose={handleDeleteCancel}>
        <DialogTitle>Confirmar Exclusão</DialogTitle>
        <DialogContent>
          <Typography>
            Tem certeza que deseja excluir o fornecedor "{fornecedorToDelete?.nome}"?
            Esta ação não pode ser desfeita.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel}>Cancelar</Button>
          <Button onClick={handleDeleteConfirm} color="error" variant="contained">
            Excluir
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default FornecedoresPage;
